import type { IntlShape } from "react-intl";
import { isoDate } from "@/lib/utils";
import type { AppointmentStatus } from "@/types/appointments/api-types/appointment-types";

type AppointmentToastAction = "updated" | AppointmentStatus;
type AppointmentToastVariant = "success" | "error";

export const APPOINTMENT_NOTES_TOAST = {
  success: "Appointment notes saved",
  error: "Failed to save appointment notes",
} as const;

/** Sonner default is 4000ms — these strings include client and date. */
export const APPOINTMENT_TOAST_DURATION_MS = 8000;

const TOAST_PHRASES: Record<
  AppointmentToastAction,
  { success: string; error: string }
> = {
  booked: { success: "booked", error: "book appointment" },
  updated: { success: "updated", error: "update appointment" },
  confirmed: { success: "confirmed", error: "confirm appointment" },
  cancelled: { success: "cancelled", error: "cancel appointment" },
  completed: { success: "completed", error: "complete appointment" },
  arrived: {
    success: "marked as arrived",
    error: "mark appointment as arrived",
  },
  left_message: {
    success: "marked as left message",
    error: "mark appointment as left message",
  },
  no_show: {
    success: "marked as no show",
    error: "mark appointment as no show",
  },
};

const displayClientName = (clientName: string | null | undefined): string => {
  const trimmed = clientName?.trim();
  return trimmed ? trimmed : "this client";
};

export const appointmentToastMessage = ({
  intl,
  action,
  variant,
  clientName,
  appointmentDate,
}: {
  intl: IntlShape;
  action: AppointmentToastAction;
  variant: AppointmentToastVariant;
  clientName: string | null | undefined;
  appointmentDate: Date | string;
}): string => {
  const client = displayClientName(clientName);
  const date = intl.formatDate(
    typeof appointmentDate === "string" ? isoDate(appointmentDate) : appointmentDate,
    { format: "long" }
  );
  const phrases = TOAST_PHRASES[action];
  if (variant === "success") {
    return `Appointment ${phrases.success} for ${client} on ${date}`;
  }
  return `Failed to ${phrases.error} for ${client} on ${date}`;
};
